import { getStudentPhotoUrl } from '../utils/studentPhoto';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Typography, Box, Button, TextField, Grid,
    MenuItem, CircularProgress, Card, CardContent,
    Avatar, Stack, Collapse, useMediaQuery, useTheme
} from '@mui/material';
import {
    Edit, Person, ExpandMore, ExpandLess, PhotoCamera, Close as CloseIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const StudentForm = ({
    open,
    onToggle,
    editingStudent,
    onSuccess,
    onCancel,
    classes = [],
    academicYears = []
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(false);
    const [profilePictureFile, setProfilePictureFile] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState('');
    const [form, setForm] = useState({
        name: '',
        email: '',
        password: '',
        gender: '',
        dateOfBirth: '',
        classId: '',
        academicYear: '',
        guardianPhone: '',
        guardianPhone2: '',
        guardianOccupation: '',
        religion: '',
        hometown: '',
        languageSpoken: '',
        fatherName: '',
        fatherOccupation: '',
        motherName: '',
        motherOccupation: '',
        admissionNumber: ''
    });
    const [errors, setErrors] = useState({});

    // Initialize form or reset
    useEffect(() => {
        if (editingStudent) {
            setForm({
                name: editingStudent.user?.name || '',
                email: editingStudent.user?.email || '',
                password: '',
                gender: editingStudent.gender || '',
                dateOfBirth: editingStudent.dateOfBirth?.split('T')[0] || '',
                classId: editingStudent.class?._id || '',
                academicYear: editingStudent.academicYear || (academicYears.length > 0 ? academicYears[0] : ''),
                academicYear: editingStudent.academicYear || (academicYears.length > 0 ? academicYears[0] : ''),
                guardianPhone: editingStudent.guardianPhone || '',
                guardianPhone2: editingStudent.guardianPhone2 || '',
                guardianOccupation: editingStudent.guardianOccupation || '',
                religion: editingStudent.religion || '',
                hometown: editingStudent.hometown || '',
                languageSpoken: editingStudent.languageSpoken || '',
                fatherName: editingStudent.fatherName || '',
                fatherOccupation: editingStudent.fatherOccupation || '',
                motherName: editingStudent.motherName || '',
                motherOccupation: editingStudent.motherOccupation || '',
                admissionNumber: editingStudent.admissionNumber || ''
            });
            setProfilePicturePreview(getStudentPhotoUrl(editingStudent) || '');
            setProfilePictureFile(null);
        } else {
            // Reset to defaults
            setForm({
                name: '',
                email: '',
                password: '',
                gender: '',
                dateOfBirth: '',
                classId: '',
                academicYear: academicYears.length > 0 ? academicYears[0] : '',
                guardianPhone: '',
                guardianPhone2: '',
                guardianOccupation: '',
                religion: '',
                hometown: '',
                languageSpoken: '',
                fatherName: '',
                fatherOccupation: '',
                motherName: '',
                motherOccupation: '',
                admissionNumber: ''
            });
            setProfilePicturePreview('');
            setProfilePictureFile(null);
        }
    }, [editingStudent, academicYears]); // Depend on academicYears to set default if loaded later

    const validateForm = () => {
        const newErrors = {
            name: !form.name,
            email: !form.email || !/\S+@\S+\.\S+/.test(form.email),
            password: !editingStudent && (!form.password || form.password.length < 6),
            gender: !form.gender,
            dateOfBirth: !form.dateOfBirth,
            classId: !form.classId,
            academicYear: !form.academicYear,
            guardianPhone: !form.guardianPhone
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: name === 'name' ? value.toUpperCase() : value }));
        setErrors(prev => ({ ...prev, [name]: false }));
    }, []);

    const handleClear = () => {
        setForm({
            name: '',
            email: '',
            password: '',
            gender: '',
            dateOfBirth: '',
            classId: '',
            academicYear: academicYears.length > 0 ? academicYears[0] : '',
            guardianPhone: '',
            guardianPhone2: '',
            guardianOccupation: '',
            religion: '',
            hometown: '',
            languageSpoken: '',
            fatherName: '',
            fatherOccupation: '',
            motherName: '',
            motherOccupation: '',
            admissionNumber: ''
        });
        setErrors({});
        setProfilePicturePreview('');
        setProfilePictureFile(null);
        if (onCancel) onCancel();
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePictureFile(file);
            setProfilePicturePreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveImage = () => {
        setProfilePictureFile(null);
        setProfilePicturePreview('');
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateForm()) {
            toast.error('Please fix validation errors');
            return;
        }

        setLoading(true);
        const formData = new FormData();
        Object.keys(form).forEach(key => {
            if (form[key]) {
                formData.append(key, form[key]);
            }
        });
        if (profilePictureFile) {
            formData.append('profilePicture', profilePictureFile);
        }

        try {
            if (editingStudent) {
                await api.put(`/students/${editingStudent._id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Student updated successfully');
            } else {
                await api.post('/students', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Student created successfully');
            }
            handleClear();
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Error saving student:', err.response?.data || err.message);
            toast.error(err.response?.data?.message || 'Failed to save student');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{
            mb: 4,
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.divider}`
        }}>
            <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
                <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    spacing={isMobile ? 2 : 2}
                    sx={{ mb: 3 }}
                >
                    <Avatar
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            width: isMobile ? 40 : 48,
                            height: isMobile ? 40 : 48
                        }}
                    >
                        {editingStudent ? <Edit /> : <Person />}
                    </Avatar>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        fontWeight="600"
                        sx={{ fontSize: isMobile ? '1.125rem' : '1.5rem' }}
                    >
                        {editingStudent ? 'Edit Student' : 'Add New Student'}
                    </Typography>
                    <Button
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        startIcon={open ? <ExpandLess /> : <ExpandMore />}
                        onClick={onToggle}
                        sx={{ ml: isMobile ? 0 : 'auto', mt: isMobile ? 1 : 0 }}
                    >
                        {open ? 'Collapse' : 'Expand'}
                    </Button>
                </Stack>

                <Collapse in={open}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle2" color="primary" fontWeight="bold">Student Information</Typography>
                            </Grid>

                            {/* Profile Picture Upload */}
                            <Grid size={{ xs: 12 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box position="relative">
                                        <Avatar
                                            src={profilePicturePreview}
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                bgcolor: theme.palette.grey[200],
                                                border: `2px solid ${theme.palette.primary.main}`
                                            }}
                                        >
                                            {!profilePicturePreview && <Person sx={{ fontSize: 40, color: theme.palette.text.secondary }} />}
                                        </Avatar>
                                        {profilePicturePreview && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="error"
                                                sx={{
                                                    position: 'absolute',
                                                    top: -5,
                                                    right: -5,
                                                    minWidth: 'auto',
                                                    p: 0.5,
                                                    borderRadius: '50%'
                                                }}
                                                onClick={handleRemoveImage}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </Button>
                                        )}
                                    </Box>
                                    <Box>
                                        <input
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            id="profile-picture-upload"
                                            type="file"
                                            onChange={handleImageChange}
                                        />
                                        <label htmlFor="profile-picture-upload">
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                startIcon={<PhotoCamera />}
                                                size="small"
                                            >
                                                {profilePicturePreview ? 'Change Photo' : 'Upload Photo (Optional)'}
                                            </Button>
                                        </label>
                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Max size: 2MB. Format: JPG/PNG
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    error={errors.name}
                                    helperText={errors.name ? 'Name is required' : "Enter student's full name"}
                                    disabled={loading}
                                    placeholder="Enter student's full name"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    error={errors.email}
                                    helperText={errors.email ? 'Valid email is required' : "Enter student's email address"}
                                    disabled={loading}
                                    placeholder="Enter student's email"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Admission Number"
                                    name="admissionNumber"
                                    value={form.admissionNumber}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="Leave blank to auto-generate"
                                    size={isMobile ? "small" : "medium"}
                                    helperText="Manual ID from physical register"
                                />
                            </Grid>

                            {!editingStudent && (
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        name="password"
                                        type="password"
                                        value={form.password}
                                        onChange={handleChange}
                                        error={errors.password}
                                        helperText={errors.password ? 'Minimum 6 characters required' : "Set a password for the student"}
                                        disabled={loading}
                                        placeholder="Create a secure password"
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                            )}

                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Gender"
                                    name="gender"
                                    value={form.gender}
                                    onChange={handleChange}
                                    error={errors.gender}
                                    helperText={errors.gender ? 'Gender is required' : "Select student's gender"}
                                    disabled={loading}
                                    InputLabelProps={{ shrink: true }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    <MenuItem value="" disabled>Select Gender</MenuItem>
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Date of Birth"
                                    name="dateOfBirth"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={form.dateOfBirth}
                                    onChange={handleChange}
                                    error={errors.dateOfBirth}
                                    helperText={errors.dateOfBirth ? 'Date of birth is required' : "Select student's date of birth"}
                                    disabled={loading}
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Academic Year"
                                    name="academicYear"
                                    value={form.academicYear}
                                    onChange={handleChange}
                                    error={errors.academicYear}
                                    helperText={errors.academicYear ? 'Academic year is required' : "Select academic year"}
                                    disabled={loading || academicYears.length === 0}
                                    InputLabelProps={{ shrink: true }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    <MenuItem value="" disabled>Select Academic Year</MenuItem>
                                    {useMemo(() => academicYears.map(year => (
                                        <MenuItem key={year} value={year}>{year}</MenuItem>
                                    )), [academicYears])}
                                </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Class"
                                    name="classId"
                                    value={form.classId}
                                    onChange={handleChange}
                                    error={errors.classId}
                                    helperText={errors.classId ? 'Class is required' : "Select student's class"}
                                    disabled={loading || classes.length === 0}
                                    InputLabelProps={{ shrink: true }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    <MenuItem value="" disabled>Select Class</MenuItem>
                                    {useMemo(() => classes.map(cls => (
                                        <MenuItem key={cls._id} value={cls._id}>{cls.displayName || cls.name}</MenuItem>
                                    )), [classes])}
                                </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Religion"
                                    name="religion"
                                    value={form.religion}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="e.g. Christianity, Islam"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Home Town"
                                    name="hometown"
                                    value={form.hometown}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="Enter home town"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Language Spoken"
                                    name="languageSpoken"
                                    value={form.languageSpoken}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="e.g. English, Twi"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 2 }}>Parent/Guardian Information</Typography>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Father/Guardian's Name"
                                    name="fatherName"
                                    value={form.fatherName}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="Enter father's name"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Father's Occupation"
                                    name="fatherOccupation"
                                    value={form.fatherOccupation}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="Enter father's occupation"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Mother/Guardian's Name"
                                    name="motherName"
                                    value={form.motherName}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="Enter mother's name"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Mother's Occupation"
                                    name="motherOccupation"
                                    value={form.motherOccupation}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="Enter mother's occupation"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Primary Contact (Phone)"
                                    name="guardianPhone"
                                    value={form.guardianPhone}
                                    onChange={handleChange}
                                    error={errors.guardianPhone}
                                    helperText={errors.guardianPhone ? 'Contact phone is required' : "Enter primary contact number"}
                                    disabled={loading}
                                    placeholder="e.g. 024XXXXXXX"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Secondary Contact (Phone 2)"
                                    name="guardianPhone2"
                                    value={form.guardianPhone2}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="e.g. 055XXXXXXX"
                                    size={isMobile ? "small" : "medium"}
                                    helperText="Optional secondary contact number"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Guardian Occupation (if different)"
                                    name="guardianOccupation"
                                    value={form.guardianOccupation}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="Enter guardian's occupation"
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Stack
                                    direction={isMobile ? "column" : "row"}
                                    spacing={2}
                                    justifyContent="flex-end"
                                    sx={{ gap: isMobile ? 1 : 2 }}
                                >
                                    {editingStudent && (
                                        <Button
                                            onClick={handleClear}
                                            variant="outlined"
                                            disabled={loading}
                                            fullWidth={isMobile}
                                            size={isMobile ? "medium" : "large"}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size={isMobile ? "medium" : "large"}
                                        disabled={loading}
                                        startIcon={loading ? <CircularProgress size={20} /> : null}
                                        fullWidth={isMobile}
                                    >
                                        {loading ? 'Saving...' : editingStudent ? 'Update Student' : 'Create Student'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default StudentForm;
